<template>
    <div class="message-list-container">
        <!-- 顶部工具栏 -->
        <div class="toolbar">
            <el-input v-model="searchQuery" placeholder="搜索留言标题或内容" clearable @keyup.enter="fetchMessages"
                class="search-input" size="large">
                <template #append>
                    <el-button @click="fetchMessages" type="primary">
                        <el-icon>
                            <Search />
                        </el-icon>
                    </el-button>
                </template>
            </el-input>
            <el-button type="primary" @click="openCreateDialog" size="large">
                <el-icon>
                    <Plus />
                </el-icon> 创建留言
            </el-button>
        </div>

        <!-- 过滤和排序控制 -->
        <div class="filter-controls">
            <div class="sort-controls">
                <span class="sort-label">排序方式：</span>
                <el-radio-group v-model="currentSort.field" @change="handleSortChange">
                    <el-radio-button label="createTime">创建时间</el-radio-button>
                    <el-radio-button label="title">标题</el-radio-button>
                </el-radio-group>
                <el-button @click="toggleSortDirection" class="sort-direction" circle>
                    <el-icon v-if="currentSort.direction === 'asc'">
                        <SortAscending />
                    </el-icon>
                    <el-icon v-else>
                        <SortDescending />
                    </el-icon>
                </el-button>
            </div>

            <div class="visibility-filter">
                <el-radio-group v-model="visibilityFilter" @change="handleFilterChange">
                    <el-radio-button label="all">全部留言</el-radio-button>
                    <el-radio-button label="public">公开留言</el-radio-button>
                    <el-radio-button label="private">私密留言</el-radio-button>
                </el-radio-group>
            </div>
        </div>

        <!-- 留言列表 -->
        <div class="message-grid">
            <template v-if="filteredAndSortedMessages.length > 0">
                <el-card v-for="message in filteredAndSortedMessages" :key="message.id" class="message-card"
                    shadow="hover">
                    <template #header>
                        <div class="card-header">
                            <div class="title-container">
                                <el-icon class="message-icon">
                                    <ChatRound v-if="message.isPublic === '1'" />
                                    <Lock v-else />
                                </el-icon>
                                <span class="message-title" :title="message.title">{{ message.title }}</span>
                            </div>
                            <el-tag :type="message.isPublic === '1' ? 'success' : 'info'" size="small"
                                class="status-tag">
                                {{ message.isPublic === '1' ? '公开' : '私密' }}
                            </el-tag>
                        </div>
                    </template>

                    <div class="message-content">
                        <el-scrollbar max-height="120px">
                            <p class="content-preview">{{ message.content }}</p>
                        </el-scrollbar>
                        <div class="message-meta">
                            <div class="meta-row">
                                <span class="message-id">
                                    <el-icon>
                                        <Discount />
                                    </el-icon> ID: {{ message.messageId }}
                                </span>
                                <el-tooltip :content="'更新者: ' + message.updateBy" placement="top"
                                    v-if="message.updateBy">
                                    <el-icon>
                                        <User />
                                    </el-icon>
                                </el-tooltip>
                            </div>
                            <div class="meta-row">
                                <span class="message-time">
                                    <el-icon>
                                        <Clock />
                                    </el-icon> {{ formatDate(message.createTime) }}
                                </span>
                                <el-tag size="small" v-if="message.isTop === '1'" type="warning">置顶</el-tag>
                            </div>
                        </div>
                    </div>

                    <div class="card-actions">
                        <el-button size="small" @click="viewMessageDetail(message)" class="action-button"
                            :type="currentViewing === message.messageId ? 'primary' : ''">
                            <el-icon>
                                <Document />
                            </el-icon> 详情
                        </el-button>
                        <el-button size="small" type="warning" @click="toggleMessageVisibility(message)"
                            class="action-button">
                            <el-icon>
                                <Lock v-if="message.isPublic === '0'" />
                                <Unlock v-else />
                            </el-icon>
                            {{ message.isPublic === '1' ? '设为私密' : '设为公开' }}
                        </el-button>
                        <el-button size="small" type="info" @click="openShareDialog(message)" class="action-button">
                            <el-icon>
                                <Share />
                            </el-icon> 分享
                        </el-button>
                        <el-button v-if="canDelete()" size="small" type="danger" @click="deleteMessage(message)" class="action-button">
                            <el-icon>
                                <Delete />
                            </el-icon> 删除
                        </el-button>
                    </div>
                </el-card>
            </template>

            <div v-else class="no-results">
                <el-empty description="没有找到符合条件的留言">
                    <el-button type="primary" @click="resetFilters">重置筛选条件</el-button>
                </el-empty>
            </div>
        </div>

        <!-- 分页控件 -->
        <div class="pagination-container">
            <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize" :page-sizes="[10, 20, 30]"
                layout="total, sizes, prev, pager, next, jumper" :total="total" @size-change="handleSizeChange"
                @current-change="handleCurrentChange" background />
        </div>

        <!-- 详情对话框 -->
        <el-dialog v-model="detailDialogVisible" :title="`留言详情`" width="60%" class="detail-dialog"
            @close="closeDetailDialog">
            <div class="detail-container">
                <el-scrollbar height="40vh">
                    <!-- 当前留言详情 -->
                    <div class="message-detail">
                        <div class="detail-header">
                            <div class="detail-title">
                                <span>{{ currentMessageData?.title }}</span>
                            </div>
                            <div class="detail-meta">
                                <span class="meta-item">
                                    <el-icon>
                                        <Discount />
                                    </el-icon> ID: {{ currentMessageData?.messageId }}
                                </span>
                                <span class="meta-item">
                                    <el-icon>
                                        <Clock />
                                    </el-icon> {{ formatDate(currentMessageData?.createTime) }}
                                </span>
                            </div>
                        </div>

                        <div class="detail-content-container">
                            <div class="detail-content">{{ currentMessageData?.content }}</div>
                        </div>
                    </div>
                </el-scrollbar>

                <!-- 美化后的翻页控制 -->
                <div class="pagination-controls-bottom">
                    <el-button @click="goToPrevMessage" :disabled="currentMessageIndex === 0" circle
                        class="page-button prev-button" size="large">
                        <el-icon size="20">
                            <ArrowLeft />
                        </el-icon>
                    </el-button>

                    <div class="page-indicator">
                        <span class="current-page">{{ currentMessageIndex + 1 }}</span>
                        <span class="separator">/</span>
                        <span class="total-pages">{{ currentMessages.length }}</span>
                    </div>

                    <el-button @click="goToNextMessage" :disabled="currentMessageIndex === currentMessages.length - 1"
                        circle class="page-button next-button" size="large">
                        <el-icon size="20">
                            <ArrowRight />
                        </el-icon>
                    </el-button>
                </div>
            </div>
            <template #footer>
                <div class="dialog-footer">
                    <el-button @click="closeDetailDialog">关闭</el-button>
                </div>
            </template>
        </el-dialog>

        <!-- 分享对话框 -->
        <el-dialog v-model="shareDialogVisible" :title="`分享留言 - ${shareTitle}`" width="40%" class="share-dialog">
            <el-tabs v-model="activeTab" stretch>
                <el-tab-pane label="链接分享" name="link">
                    <div class="link-share">
                        <el-input v-model="shareLink" readonly class="share-link-input" size="large">
                            <template #prepend>
                                <el-icon>
                                    <Link />
                                </el-icon>
                            </template>
                        </el-input>
                        <el-button type="primary" @click="copyLink" class="copy-button" size="large">
                            <el-icon>
                                <DocumentCopy />
                            </el-icon> 复制链接
                        </el-button>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="二维码分享" name="qrcode">
                    <div class="qrcode-container">
                        <div class="qrcode-card" v-show="showCanvas">
                            <canvas ref="qrcodeCanvas"></canvas>
                            <div class="qrcode-info">
                                <div class="qrcode-title">{{ shareTitle }}</div>
                                <div class="qrcode-url">{{ shareLink }}</div>
                            </div>
                        </div>
                        <div class="qrcode-placeholder" v-show="!showCanvas">
                            <div class="loading-spinner">
                                <el-icon :size="40">
                                    <Loading />
                                </el-icon>
                            </div>
                        </div>

                        <div class="qrcode-actions">
                            <el-button type="primary" @click="saveQrcode" :loading="savingQrcode" class="save-button"
                                size="large">
                                <el-icon>
                                    <Download />
                                </el-icon> 保存图片
                            </el-button>
                            <el-button type="info" @click="printQrcode" class="print-button" size="large">
                                <el-icon>
                                    <Printer />
                                </el-icon> 打印二维码
                            </el-button>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>
            <template #footer>
                <div class="dialog-footer">
                    <el-button @click="shareDialogVisible = false">关闭</el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { canDelete } from '../../../util/permission'
import {
    ElMessage,
    ElMessageBox,
    ElScrollbar
} from 'element-plus';
import {
    Search, Plus, Document, Share, DocumentCopy, Download, Delete,
    ChatRound, Lock, Discount, Clock, User, SortAscending, SortDescending,
    Loading, Link, Printer, ArrowLeft, ArrowRight, Unlock
} from '@element-plus/icons-vue';
import QRCode from 'qrcode';
import axios from 'axios';
import { useRouter } from 'vue-router';
import { escapeHtml } from '../../../util/escapeHtml';

const router = useRouter();

// 状态管理
const messageList = ref([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);
const searchQuery = ref('');
const loading = ref(false);
const detailDialogVisible = ref(false);
const currentMessage = ref({});
const currentMessages = ref([]);
const shareDialogVisible = ref(false);
const activeTab = ref('link');
const shareLink = ref('');
const shareTitle = ref('');
const qrcodeCanvas = ref(null);
const showCanvas = ref(false);
const savingQrcode = ref(false);
const currentViewing = ref(null);
const visibilityFilter = ref('all');
const isMobile = ref(false);

// 排序控制
const currentSort = ref({ field: 'createTime', direction: 'desc' });

// 检查设备类型
const checkIsMobile = () => {
    isMobile.value = window.innerWidth < 768;
};

// 计算属性：过滤和排序后的留言列表
const filteredMessages = computed(() => {
    if (!messageList.value || messageList.value.length === 0) return [];

    let filtered = [...messageList.value];

    // 应用可见性筛选
    if (visibilityFilter.value === 'public') {
        filtered = filtered.filter(m => m.isPublic === '1');
    } else if (visibilityFilter.value === 'private') {
        filtered = filtered.filter(m => m.isPublic === '0');
    }

    // 应用搜索查询
    const query = searchQuery.value.toLowerCase();
    if (query) {
        filtered = filtered.filter(message =>
            message.title.toLowerCase().includes(query) ||
            message.content.toLowerCase().includes(query) ||
            message.messageId.includes(query)
        );
    }

    return filtered;
});

// 计算属性：排序后的留言列表
const sortedMessages = computed(() => {
    const messages = filteredMessages.value;
    if (messages.length <= 1) return messages;

    return [...messages].sort((a, b) => {
        const { field, direction } = currentSort.value;
        const dir = direction === 'asc' ? 1 : -1;

        // 处理时间类型排序
        if (field === 'createTime') {
            return dir * (new Date(a[field]) - new Date(b[field]));
        }

        // 处理字符串类型排序
        if (a[field] < b[field]) return -1 * dir;
        if (a[field] > b[field]) return 1 * dir;
        return 0;
    });
});

// 说明：列表数据由服务端分页（fetchMessages 已带 page/pageSize/query），
// 前端**不能**再按 currentPage 切一次 —— 那样第 2 页拿到的是「服务端第 2 页的 10 条」再切 [10,20)，必然是空列表。
const filteredAndSortedMessages = computed(() => sortedMessages.value);

// 生命周期钩子
onMounted(() => {
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    fetchMessages();
});

// 离开页面时必须摘掉监听，否则反复进出会越挂越多
onUnmounted(() => {
    window.removeEventListener('resize', checkIsMobile);
});

// 获取留言列表
const fetchMessages = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/adminapi/message/list', {
            params: {
                page: currentPage.value,
                pageSize: pageSize.value,
                query: searchQuery.value
            }
        });

        if (response.data.code === 200) {
            messageList.value = response.data.data.records;
            total.value = response.data.data.total;
        } else {
            ElMessage.error(response.data.message || '获取留言列表失败');
        }
    } catch (error) {
        console.error('获取留言列表失败:', error);
        ElMessage.error('网络错误，请检查网络连接');
    } finally {
        loading.value = false;
    }
};

// 查看留言详情
const viewMessageDetail = async (message) => {
    try {
        currentViewing.value = message.messageId;
        // 显示加载状态
        loading.value = true;

        // 设置当前消息
        currentMessage.value = { ...message };

        // 调用API获取详情数据
        const response = await axios.get(`/adminapi/words/${message.messageId}`);

        // 提取results数组
        currentMessages.value = response.data.results || [];
        console.log(currentMessages.value.length)
        if (currentMessages.value.length === 0) {
            ElMessage.error('没有留言数据哦~');
            loading.value = false;
            return
        }
        // 按时间排序，最新的在最前面
        currentMessages.value.sort((a, b) => {
            // 假设时间字段是 createTime，格式为 "YYYY-MM-DD HH:MM:SS"
            // 如果时间格式不同，需要相应调整解析方式
            const timeA = new Date(a.createTime).getTime();
            const timeB = new Date(b.createTime).getTime();
            return timeB - timeA; // 降序排列
        });

        // 显示对话框
        detailDialogVisible.value = true;

    } catch (error) {
        console.error('获取留言详情失败:', error);
        ElMessage.error('网络错误，请检查网络连接');
    } finally {
        loading.value = false;
    }
};

// 打开分享对话框
const openShareDialog = async (message) => {
    shareLink.value = `${window.location.origin}/#/msgbox/message/${message.messageId}`;
    shareTitle.value = message.title;
    shareDialogVisible.value = true;
    activeTab.value = 'link';

    // 预生成二维码
    if (qrcodeCanvas.value) {
        showCanvas.value = false;
        setTimeout(() => generateQrcode(), 100);
    }
};

// 生成二维码
const generateQrcode = async () => {
    if (!shareLink.value) return;

    try {
        showCanvas.value = false;
        await nextTick();

        if (qrcodeCanvas.value) {
            await QRCode.toCanvas(qrcodeCanvas.value, shareLink.value, {
                width: 200,
                margin: 1,
                color: {
                    dark: '#303133',
                    light: '#ffffff'
                },
                errorCorrectionLevel: 'H'
            });

            showCanvas.value = true;
        }
    } catch (error) {
        console.error('生成二维码失败:', error);
        ElMessage.error('生成二维码失败，请重试');
    }
};

// 监听标签切换
watch(activeTab, (newVal) => {
    if (newVal === 'qrcode' && shareDialogVisible.value) {
        generateQrcode();
    }
});

// 复制链接
const copyLink = () => {
    if (!shareLink.value) {
        ElMessage.warning('分享链接未生成');
        return;
    }

    // 尝试使用现代API
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareLink.value)
            .then(() => {
                ElMessage.success('链接已复制到剪贴板');
            })
            .catch(err => {
                console.error('Clipboard API 复制失败:', err);
                // 降级到传统方法
                fallbackCopyText(shareLink.value);
            });
    } else {
        // 使用传统方法
        fallbackCopyText(shareLink.value);
    }
};

// 降级复制方法（兼容旧浏览器）
const fallbackCopyText = (text) => {
    try {
        // 创建临时textarea元素
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.width = '2em';
        textarea.style.height = '2em';
        textarea.style.padding = '0';
        textarea.style.border = 'none';
        textarea.style.outline = 'none';
        textarea.style.boxShadow = 'none';
        textarea.style.background = 'transparent';
        textarea.style.opacity = '0';
        
        document.body.appendChild(textarea);
        
        // 选中文本
        textarea.focus();
        textarea.select();
        
        // 尝试复制
        const successful = document.execCommand('copy');
        
        // 移除临时元素
        document.body.removeChild(textarea);
        
        if (successful) {
            ElMessage.success('链接已复制到剪贴板');
        } else {
            throw new Error('execCommand 复制失败');
        }
    } catch (err) {
        console.error('降级复制方法失败:', err);
        ElMessage.error('复制失败，请手动复制链接');
        
        // 最后的降级方案：选中输入框文本
        nextTick(() => {
            const input = document.querySelector('.share-link-input input');
            if (input) {
                input.select();
                ElMessage.info('请按 Ctrl+C (Mac: Cmd+C) 手动复制');
            }
        });
    }
};

// 保存二维码图片
const saveQrcode = async () => {
    if (!qrcodeCanvas.value || !showCanvas.value) {
        ElMessage.warning('二维码未生成，请稍后再试');
        return;
    }

    savingQrcode.value = true;

    try {
        // 确保Canvas完全渲染
        await new Promise(resolve => setTimeout(resolve, 100));

        // 转换为Blob对象
        qrcodeCanvas.value.toBlob((blob) => {
            if (!blob) {
                throw new Error('无法生成图片数据');
            }

            // 创建下载链接
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `留言二维码_${shareTitle.value || new Date().getTime()}.png`;
            document.body.appendChild(a);
            a.click();

            // 清理
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                savingQrcode.value = false;
            }, 100);

            ElMessage.success('二维码图片保存成功');
        }, 'image/png', 1.0);
    } catch (error) {
        console.error('保存二维码失败:', error);
        ElMessage.error('保存二维码失败: ' + error.message);
        savingQrcode.value = false;
    }
};

// 打印二维码
const printQrcode = () => {
    const win = window.open('', 'PRINT', 'height=600,width=800');
    win.document.write(`
    <html>
      <head>
        <title>${escapeHtml(shareTitle.value || '留言二维码')}打印</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            display: flex; 
            flex-direction: column;
            align-items: center; 
            justify-content: center; 
            padding: 20px;
          }
          .print-title { font-size: 24px; margin-bottom: 20px; }
          .print-qrcode { width: 300px; height: 300px; margin: 20px auto; }
          .print-url { margin-top: 15px; color: #777; }
        </style>
      </head>
      <body>
        <h1 class="print-title">${escapeHtml(shareTitle.value || '留言详情')}</h1>
        <div class="print-qrcode">${qrcodeCanvas.value.outerHTML}</div>
        <p class="print-url">${escapeHtml(shareLink.value || '')}</p>
        <script>
          window.onload = function() { 
            window.print(); 
            window.close(); 
          }
        <\/script>
      </body>
    </html>
  `);
    win.document.close();
};

// 分页控制（前提：数据来自服务端，翻页必须重新请求）
const handleSizeChange = (newSize) => {
    pageSize.value = newSize;
    currentPage.value = 1;
    fetchMessages();
};

const handleCurrentChange = (newPage) => {
    currentPage.value = newPage;
    fetchMessages();
};

// 排序处理
const handleSortChange = () => {
    currentPage.value = 1;
};

const toggleSortDirection = () => {
    currentSort.value.direction = currentSort.value.direction === 'asc' ? 'desc' : 'asc';
    currentPage.value = 1;
};

// 筛选处理
const handleFilterChange = () => {
    currentPage.value = 1;
};

// 重置筛选条件
const resetFilters = () => {
    searchQuery.value = '';
    currentSort.value = { field: 'createTime', direction: 'desc' };
    visibilityFilter.value = 'all';
    currentPage.value = 1;
};

// 日期格式化函数
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    // 非法时间串会让 Intl.format 抛 RangeError，先做有效性校验
    if (isNaN(date.getTime())) return dateStr || '';

    const formatter = new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });

    return formatter.format(date);
};

// 打开创建留言对话框
const openCreateDialog = () => {
    router.push('/admin/message/add');
};

// 删除留言
const deleteMessage = (message) => {
    ElMessageBox.confirm(
        '确定要删除这条留言吗？此操作不可恢复。',
        '删除确认',
        {
            confirmButtonText: '确认删除',
            cancelButtonText: '取消',
            type: 'warning',
            lockScroll: false,
            customClass: 'delete-confirm-box'
        }
    ).then(async () => {
        try {
            await axios.delete(`/adminapi/message/${message.messageId}`);
            ElMessage.success('删除成功');
            fetchMessages();
        } catch (error) {
            console.error('删除失败:', error);
            ElMessage.error('删除失败，请重试');
        }
    }).catch(() => {
        // 取消删除
    });
};



// 状态管理
const currentMessageIndex = ref(0);

// 计算当前显示的留言数据
const currentMessageData = computed(() => {
    return currentMessages.value[currentMessageIndex.value];
});

// 打开详情对话框
const openDetailDialog = (messages, index = 0) => {
    currentMessages.value = messages;
    currentMessageIndex.value = index;
    detailDialogVisible.value = true;
};

// 上一页
const goToPrevMessage = () => {
    if (currentMessageIndex.value > 0) {
        currentMessageIndex.value--;
        scrollToTop();
    }
};

// 下一页
const goToNextMessage = () => {
    if (currentMessageIndex.value < currentMessages.value.length - 1) {
        currentMessageIndex.value++;
        scrollToTop();
    }
};

// 滚动到顶部
const scrollToTop = () => {
    const scrollbar = document.querySelector('.detail-container .el-scrollbar__wrap');
    if (scrollbar) {
        scrollbar.scrollTop = 0;
    }
};

// 关闭对话框
const closeDetailDialog = () => {
    detailDialogVisible.value = false;
};


// 暴露方法给父组件使用
defineExpose({
    openDetailDialog
});

// 切换留言可见性状态
const toggleMessageVisibility = async (message) => {
    try {
        const newStatus = message.isPublic === '1' ? '0' : '1';
        await axios.post(`/adminapi/message/visibility/${message.messageId}`, {
            isPublic: newStatus
        });

        ElMessage.success(`已${newStatus === '1' ? '公开' : '设为私密'}该留言`);
        // 更新本地数据
        message.isPublic = newStatus;
    } catch (error) {
        console.error('修改留言状态失败:', error);
        ElMessage.error('修改状态失败，请重试');
    }
};
</script>

<style scoped lang="scss">
$primary-color: #409EFF;
$success-color: #67C23A;
$warning-color: #E6A23C;
$danger-color: #F56C6C;
$info-color: #909399;

$light-bg: #f5f7fa;
$border-color: #ebeef5;
$card-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
$hover-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);

.message-list-container {
    padding: 20px;
    max-width: 1600px;
    margin: 0 auto;
    background-color: $light-bg;
    min-height: 100vh;

    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;
        background: white;
        padding: 20px;
        border-radius: 12px;
        box-shadow: $card-shadow;

        .search-input {
            width: 400px;
            border-radius: 24px;

            :deep(.el-input-group__append) {
                background-color: $primary-color;
                border-color: $primary-color;
                color: white;
                border-radius: 0 24px 24px 0;
                padding: 0 15px;
            }
        }

        .el-button {
            border-radius: 8px;
            padding: 12px 24px;
            transition: all 0.3s ease;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            }

            .el-icon {
                margin-right: 5px;
            }
        }
    }

    .filter-controls {
        display: flex;
        justify-content: space-between;
        margin-bottom: 24px;
        background: white;
        padding: 16px 20px;
        border-radius: 12px;
        box-shadow: $card-shadow;

        .sort-controls {
            display: flex;
            align-items: center;

            .sort-label {
                margin-right: 12px;
                color: #606266;
                font-weight: 500;
            }

            .el-radio-group {
                margin-right: 12px;
            }

            .sort-direction {
                transition: transform 0.3s ease;

                &:hover {
                    transform: scale(1.1);
                    background: #f5f7fa;
                }
            }
        }
    }

    .message-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        gap: 20px;
        margin-bottom: 30px;

        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }

        .no-results {
            grid-column: 1 / -1;
            background: white;
            border-radius: 12px;
            padding: 40px 0;
            text-align: center;
            box-shadow: $card-shadow;
        }

        .message-card {
            border-radius: 12px;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            overflow: hidden;
            border: none;

            &:hover {
                transform: translateY(-5px);
                box-shadow: $hover-shadow;
            }

            :deep(.el-card__header) {
                padding: 16px 20px;
                background: linear-gradient(to right, #f5f7fa, white);
                border-bottom: 1px solid $border-color;

                .card-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    .title-container {
                        display: flex;
                        align-items: center;
                        max-width: 80%;

                        .message-icon {
                            margin-right: 10px;
                            color: $primary-color;
                        }

                        .message-title {
                            font-size: 18px;
                            font-weight: 600;
                            color: #303133;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }

                    .status-tag {
                        font-weight: 500;
                        padding: 4px 12px;
                    }
                }
            }

            .message-content {
                padding: 16px 20px;

                .content-preview {
                    line-height: 1.6;
                    color: #606266;
                    font-size: 15px;
                    white-space: pre-wrap;
                    margin: 0;
                    max-height: 120px;
                    overflow: hidden;
                }

                .message-meta {
                    margin-top: 16px;
                    font-size: 13px;
                    color: $info-color;

                    .meta-row {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 8px;

                        .message-id {
                            display: flex;
                            align-items: center;

                            .el-icon {
                                margin-right: 4px;
                                font-size: 14px;
                            }
                        }

                        .message-time {
                            display: flex;
                            align-items: center;

                            .el-icon {
                                margin-right: 4px;
                                font-size: 14px;
                            }
                        }
                    }
                }
            }

            .card-actions {
                /* 保持原有样式不变 */
                padding: 0 20px 20px;
                display: flex;
                justify-content: flex-end;
                gap: 10px;

                /* 新增按钮的特殊样式 */
                .action-button[type="warning"] {
                    background-color: #e6a23c;
                    border-color: #e6a23c;
                    color: white;

                    &:hover {
                        background-color: darken(#e6a23c, 10%);
                        border-color: darken(#e6a23c, 10%);
                    }
                }
            }
        }
    }

    .pagination-container {
        display: flex;
        justify-content: center;
        padding: 20px 0;
        background: white;
        border-radius: 12px;
        box-shadow: $card-shadow;
    }

    // 详情对话框样式
    .detail-dialog {
        border-radius: 12px;

        .detail-container {
            padding: 0;

            .message-detail {
                padding: 20px;

                .detail-header {
                    margin-bottom: 24px;

                    .detail-title {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        margin-bottom: 16px;

                        span {
                            font-size: 24px;
                            font-weight: 600;
                            color: #303133;
                        }
                    }

                    .detail-meta {
                        display: flex;
                        gap: 20px;
                        color: $info-color;
                        font-size: 14px;

                        .meta-item {
                            display: flex;
                            align-items: center;

                            .el-icon {
                                margin-right: 6px;
                                font-size: 16px;
                            }
                        }
                    }
                }

                .detail-content-container {
                    background: $light-bg;
                    padding: 20px;
                    border-radius: 8px;
                    margin-bottom: 30px;

                    .detail-content {
                        line-height: 1.8;
                        font-size: 16px;
                        color: #303133;
                        white-space: pre-wrap;
                    }
                }
            }

            .related-messages {
                .related-message-card {
                    background: white;
                    border-radius: 8px;
                    padding: 16px;
                    box-shadow: $card-shadow;
                    margin-bottom: 16px;
                    transition: all 0.3s ease;

                    &:hover {
                        transform: translateX(5px);
                        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
                    }

                    .related-header {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 10px;

                        .related-title {
                            font-weight: 600;
                            font-size: 16px;
                            color: #303133;
                        }
                    }

                    .related-content {
                        line-height: 1.5;
                        color: #262728;
                        font-size: 24px;
                        margin-bottom: 12px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                    }

                    .related-meta {
                        display: flex;
                        justify-content: space-between;
                        font-size: 12px;
                        color: $info-color;
                    }
                }
            }
        }

        .dialog-footer {
            text-align: center;

            .el-button {
                padding: 10px 24px;
                border-radius: 8px;
            }
        }
    }

    // 分享对话框样式
    .share-dialog {
        border-radius: 12px;

        .link-share {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 16px 0;

            .share-link-input {
                :deep(input) {
                    border-radius: 8px;
                    font-family: monospace;
                }

                :deep(.el-input-group__prepend) {
                    background-color: #e9f5fe;
                }
            }

            .copy-button {
                padding: 12px 24px;
                border-radius: 8px;
                transition: all 0.3s ease;

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                }
            }
        }

        .qrcode-container {
            padding: 20px 0;

            .qrcode-card {
                background: white;
                border-radius: 12px;
                padding: 20px;
                box-shadow: $card-shadow;
                display: flex;
                flex-direction: column;
                align-items: center;

                canvas {
                    width: 200px;
                    height: 200px;
                    margin-bottom: 20px;
                    border: 8px solid white;
                    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
                }

                .qrcode-info {
                    text-align: center;

                    .qrcode-title {
                        font-weight: 600;
                        font-size: 16px;
                        margin-bottom: 8px;
                        color: #303133;
                    }

                    .qrcode-url {
                        font-size: 12px;
                        color: $info-color;
                        word-break: break-all;
                        max-width: 300px;
                    }
                }
            }

            .qrcode-placeholder {
                height: 240px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: $light-bg;
                border-radius: 12px;

                .loading-spinner {
                    animation: rotate 2s linear infinite;

                    @keyframes rotate {
                        from {
                            transform: rotate(0deg);
                        }

                        to {
                            transform: rotate(360deg);
                        }
                    }
                }
            }

            .qrcode-actions {
                display: flex;
                justify-content: center;
                gap: 16px;
                margin-top: 24px;

                .el-button {
                    padding: 12px 24px;
                    border-radius: 8px;
                    transition: all 0.3s ease;

                    &:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                    }
                }

                .save-button {
                    background-color: $success-color;
                    border-color: $success-color;

                    &:hover {
                        background-color: lighten($success-color, 5%);
                    }
                }

                .print-button {
                    background-color: $primary-color;
                    border-color: $primary-color;

                    &:hover {
                        background-color: lighten($primary-color, 5%);
                    }
                }
            }
        }
    }
}
</style>

<style>
.detail-dialog .el-dialog__header {
    border-bottom: 1px solid #ebeef5;
}

.detail-dialog .el-dialog__body {
    padding: 0;
}

.delete-confirm-box {
    border-radius: 12px;
}

.delete-confirm-box .el-message-box__content {
    padding: 30px 20px;
}

.custom-divider .el-divider__text {
    font-size: 40px;
}

$primary-color: #409EFF;
$info-color: #909399;
$light-bg: #f5f7fa;
$border-color: #ebeef5;
$disabled-color: #c0c4cc;
$shadow-color: rgba(0, 0, 0, 0.1);
$hover-shadow-color: rgba(0, 0, 0, 0.15);

.detail-dialog {
    border-radius: 12px;

    .detail-container {
        display: flex;
        flex-direction: column;
        height: 100%;

        .el-scrollbar {
            flex: 1;
        }
    }
}

.pagination-controls-bottom {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0;
    border-top: 1px solid $border-color;
    margin-top: 16px;
    gap: 24px;
    background: white;
    border-radius: 0 0 12px 12px;
    box-shadow: 0 -2px 8px $shadow-color;

    .page-button {
        width: 48px;
        height: 48px;
        font-weight: bold;
        transition: all 0.3s ease;
        box-shadow: 0 2px 6px $shadow-color;
        border: none;

        &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px $hover-shadow-color;
        }

        &:active:not(:disabled) {
            transform: translateY(0);
            box-shadow: 0 2px 6px $shadow-color;
        }

        &:disabled {
            color: $disabled-color;
            cursor: not-allowed;
            opacity: 0.6;
        }

        &.prev-button {
            background: linear-gradient(135deg, #f5f7fa, #e4e7ed);

            &:hover:not(:disabled) {
                background: linear-gradient(135deg, #e4e7ed, #d3d6db);
            }
        }

        &.next-button {
            background: linear-gradient(135deg, #ecf5ff, #d9ecff);

            &:hover:not(:disabled) {
                background: linear-gradient(135deg, #d9ecff, #c6e1ff);
            }
        }
    }

    .page-indicator {
        display: flex;
        align-items: center;
        font-size: 16px;
        font-weight: 500;
        color: #606266;
        min-width: 80px;
        justify-content: center;

        .current-page {
            color: $primary-color;
            font-weight: 600;
            font-size: 18px;
            margin: 0 4px;
        }

        .separator {
            margin: 0 4px;
            color: $info-color;
        }

        .total-pages {
            margin: 0 4px;
        }
    }
}

.message-detail {
    padding: 20px;

    .detail-header {
        margin-bottom: 16px;

        .detail-title {
            font-size: 20px;
            font-weight: 600;
            color: #303133;
            margin-bottom: 8px;
        }

        .detail-meta {
            display: flex;
            gap: 20px;
            color: $info-color;
            font-size: 14px;

            .meta-item {
                display: flex;
                align-items: center;

                .el-icon {
                    margin-right: 6px;
                    font-size: 16px;
                }
            }
        }
    }

    .detail-content-container {
        padding: 16px;
        background: $light-bg;
        border-radius: 8px;

        .detail-content {
            line-height: 1.8;
            font-size: 16px;
            color: #303133;
            white-space: pre-wrap;
        }
    }
}

.detail-dialog .el-dialog__header {
    border-bottom: 1px solid #ebeef5;
    padding: 20px;
}

.detail-dialog .el-dialog__body {
    padding: 0;
}

.detail-dialog .el-dialog__footer {
    padding: 16px 20px;
    border-top: 1px solid #ebeef5;
}
</style>